import { useState } from 'react';
import { parentescos } from '@/mocks/coverly';

interface Beneficiario {
  nombre: string;
  parentesco: string;
  curp?: string;
  fechaNacimiento: string;
}

interface RegistroStep3Props {
  plan: 'familiar' | 'plus';
  nombre: string;
  onNext: (beneficiarios: Beneficiario[], isTestCard?: boolean) => void;
  onBack: () => void;
  loading?: boolean;
  errorMsg?: string;
}

const emptyBen = (): Beneficiario => ({ nombre: '', parentesco: '', curp: '', fechaNacimiento: '' });

function calcAgeBen(fechaNacimiento: string): number | null {
  if (!fechaNacimiento) return null;
  const birth = new Date(fechaNacimiento);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

export default function RegistroStep3({
  plan,
  onNext,
  onBack,
  loading: extLoading = false,
  errorMsg = '',
}: RegistroStep3Props) {
  const maxBens = plan === 'familiar' ? 2 : 3;
  const totalPeople = maxBens + 1;
  const planName = plan === 'familiar' ? 'Basico' : 'Prime';
  const planPrice = plan === 'familiar' ? 289 : 399;
  const [beneficiarios, setBeneficiarios] = useState<Beneficiario[]>([emptyBen()]);
  const [metodoPago, setMetodoPago] = useState<'tarjeta' | 'oxxo'>('tarjeta');
  const [cardData, setCardData] = useState({ numero: '', nombreTarjeta: '', expiry: '', cvv: '' });
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateBen = (i: number, key: keyof Beneficiario, val: string) => {
    const copy = [...beneficiarios];
    copy[i] = { ...copy[i], [key]: val };
    setBeneficiarios(copy);
  };

  const addBen = () => {
    if (beneficiarios.length < maxBens) setBeneficiarios([...beneficiarios, emptyBen()]);
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (metodoPago === 'tarjeta') {
      if (!cardData.numero || cardData.numero.replace(/\s/g, '').length < 16) e.numero = 'Numero de tarjeta invalido';
      if (!cardData.nombreTarjeta.trim()) e.nombreTarjeta = 'Campo requerido';
      if (!cardData.expiry || cardData.expiry.length < 5) e.expiry = 'Fecha invalida';
      if (!cardData.cvv || cardData.cvv.length < 3) e.cvv = 'CVV invalido';
    }
    beneficiarios.forEach((ben, i) => {
      const age = ben.fechaNacimiento ? calcAgeBen(ben.fechaNacimiento) : null;
      if (age !== null && age >= 18 && !ben.curp?.trim()) {
        e[`ben_${i}_curp`] = 'CURP requerida para mayores de edad';
      }
    });
    if (!aceptaTerminos) e.terminos = 'Debes aceptar los Terminos y Condiciones';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleActivar = () => {
    const cardNum = cardData.numero.replace(/\s/g, '');
    const isTestCard = cardNum === '4111111111111111';
    if (isTestCard) {
      onNext(beneficiarios, true);
      return;
    }
    if (validate()) onNext(beneficiarios, false);
  };

  const formatCard = (val: string) => {
    const nums = val.replace(/\D/g, '').slice(0, 16);
    return nums.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (val: string) => {
    const nums = val.replace(/\D/g, '').slice(0, 4);
    if (nums.length >= 2) return `${nums.slice(0, 2)}/${nums.slice(2)}`;
    return nums;
  };

  return (
    <div className="flex h-full flex-col bg-[#F6F4EE]">
      <div className="flex items-center gap-4 bg-[#F6F4EE] px-5 pb-4 pt-12">
        <button onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
          <i className="ri-arrow-left-line text-xl text-[#534AB7]" />
        </button>
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-wide text-[#7C7589]">Paso 3 de 3</p>
          <p className="text-sm font-bold text-[#120B25]">Familia y pago</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-28 pt-2">
        <h2 className="text-3xl font-black leading-tight text-[#120B25]">Completa tu membresia.</h2>
        <p className="mt-2 text-sm leading-6 text-[#625D6E]">
          Agrega a las personas cubiertas, elige como pagar y acepta el contrato de membresia.
        </p>

        <section className="mt-5 rounded-3xl bg-[#120B25] p-4 text-white shadow-lg shadow-[#120B25]/10">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-white/50">Plan seleccionado</p>
              <p className="text-xl font-black">Plan {planName}</p>
            </div>
            <div className="rounded-2xl bg-white/10 px-3 py-2 text-right">
              <p className="text-[11px] text-white/60">${planPrice}/mes</p>
              <p className="text-sm font-black">{totalPeople} personas</p>
            </div>
          </div>
        </section>

        <section className="mt-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-black text-[#120B25]">Personas cubiertas</span>
            <span className="rounded-full bg-[#EFEDFF] px-3 py-1 text-xs font-black text-[#534AB7]">
              {beneficiarios.length}/{maxBens} beneficiarios
            </span>
          </div>

          <div className="grid gap-3">
            {beneficiarios.map((ben, i) => {
              const age = ben.fechaNacimiento ? calcAgeBen(ben.fechaNacimiento) : null;
              const isMinor = age !== null && age < 18;

              return (
                <div key={i} className="rounded-3xl border border-[#E4DFD2] bg-white p-4 shadow-sm">
                  <p className="mb-3 text-xs font-black uppercase tracking-wide text-[#534AB7]">Beneficiario {i + 1}</p>
                  <div className="grid gap-3">
                    <input
                      type="text"
                      placeholder="Nombre completo"
                      value={ben.nombre}
                      onChange={(e) => updateBen(i, 'nombre', e.target.value)}
                      className="rounded-2xl border border-[#E5E0D5] px-4 py-3 text-sm outline-none focus:border-[#534AB7] focus:ring-2 focus:ring-[#534AB7]/20"
                    />
                    <select
                      value={ben.parentesco}
                      onChange={(e) => updateBen(i, 'parentesco', e.target.value)}
                      className="cursor-pointer rounded-2xl border border-[#E5E0D5] bg-white px-4 py-3 text-sm outline-none focus:border-[#534AB7]"
                    >
                      <option value="">Parentesco</option>
                      {parentescos.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <div className="grid gap-1">
                      <label className="text-xs font-bold uppercase tracking-wide text-[#7C7589]">Fecha de nacimiento</label>
                      <input
                        type="date"
                        value={ben.fechaNacimiento}
                        onChange={(e) => updateBen(i, 'fechaNacimiento', e.target.value)}
                        className="rounded-2xl border border-[#E5E0D5] bg-white px-4 py-3 text-sm outline-none focus:border-[#534AB7] focus:ring-2 focus:ring-[#534AB7]/20"
                      />
                    </div>
                    <div className="grid gap-1">
                      <div className="flex items-center gap-2">
                        <label className="flex-1 text-xs font-bold uppercase tracking-wide text-[#7C7589]">CURP</label>
                        {isMinor && (
                          <span className="rounded-full bg-[#FFF3D6] px-2 py-0.5 text-[10px] font-bold text-[#8A5A00]">
                            Opcional en menor de edad
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder={isMinor ? 'CURP opcional' : 'CURP'}
                        value={ben.curp ?? ''}
                        onChange={(e) => updateBen(i, 'curp', e.target.value.toUpperCase())}
                        className="rounded-2xl border px-4 py-3 text-sm uppercase outline-none focus:border-[#534AB7] focus:ring-2 focus:ring-[#534AB7]/20"
                        style={{ borderColor: errors[`ben_${i}_curp`] ? '#E24B4A' : '#E5E0D5' }}
                      />
                      {errors[`ben_${i}_curp`] && <p className="text-xs text-red-500">{errors[`ben_${i}_curp`]}</p>}
                    </div>
                  </div>
                </div>
              );
            })}

            {beneficiarios.length < maxBens && (
              <button
                onClick={addBen}
                className="flex w-full items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-[#534AB7] py-4 text-sm font-black text-[#534AB7] transition-colors hover:bg-[#534AB7]/5"
              >
                <i className="ri-add-line text-xl" />
                Agregar beneficiario
              </button>
            )}
          </div>
        </section>

        <section className="mt-6">
          <div className="mb-3 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#DED8CA]" />
            <span className="text-xs font-bold uppercase tracking-wide text-[#7C7589]">Metodo de pago</span>
            <div className="h-px flex-1 bg-[#DED8CA]" />
          </div>

          <div className="mb-3 grid grid-cols-2 gap-2">
            <button
              onClick={() => { setMetodoPago('tarjeta'); setErrors({}); }}
              className="flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold transition-all"
              style={{
                backgroundColor: metodoPago === 'tarjeta' ? '#534AB7' : 'white',
                color: metodoPago === 'tarjeta' ? 'white' : '#625D6E',
                border: metodoPago === 'tarjeta' ? '1px solid #534AB7' : '1px solid #E4DFD2',
              }}
            >
              <i className="ri-bank-card-line text-base" />
              Tarjeta
            </button>
            <button
              onClick={() => { setMetodoPago('oxxo'); setErrors({}); }}
              className="flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold transition-all"
              style={{
                backgroundColor: metodoPago === 'oxxo' ? '#534AB7' : 'white',
                color: metodoPago === 'oxxo' ? 'white' : '#625D6E',
                border: metodoPago === 'oxxo' ? '1px solid #534AB7' : '1px solid #E4DFD2',
              }}
            >
              <i className="ri-store-2-line text-base" />
              OXXO
            </button>
          </div>

          <div className="grid gap-3 rounded-3xl border border-[#E4DFD2] bg-white p-4 shadow-sm">
            {metodoPago === 'tarjeta' ? (
              <>
                <div className="grid gap-1">
                  <div className="flex items-center overflow-hidden rounded-2xl border focus-within:border-[#534AB7] focus-within:ring-2 focus-within:ring-[#534AB7]/20" style={{ borderColor: errors.numero ? '#E24B4A' : '#E5E0D5' }}>
                    <div className="flex h-11 w-11 items-center justify-center text-[#7C7589]">
                      <i className="ri-bank-card-line" />
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Numero de tarjeta"
                      value={cardData.numero}
                      onChange={(e) => setCardData({ ...cardData, numero: formatCard(e.target.value) })}
                      className="min-w-0 flex-1 py-3 pr-4 text-sm outline-none"
                    />
                  </div>
                  {errors.numero && <p className="text-xs text-red-500">{errors.numero}</p>}
                </div>
                <input
                  type="text"
                  placeholder="Nombre en la tarjeta"
                  value={cardData.nombreTarjeta}
                  onChange={(e) => setCardData({ ...cardData, nombreTarjeta: e.target.value.toUpperCase() })}
                  className="rounded-2xl border px-4 py-3 text-sm uppercase outline-none focus:border-[#534AB7]"
                  style={{ borderColor: errors.nombreTarjeta ? '#E24B4A' : '#E5E0D5' }}
                />
                {errors.nombreTarjeta && <p className="text-xs text-red-500">{errors.nombreTarjeta}</p>}
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="MM/AA"
                    value={cardData.expiry}
                    onChange={(e) => setCardData({ ...cardData, expiry: formatExpiry(e.target.value) })}
                    className="min-w-0 rounded-2xl border px-4 py-3 text-sm outline-none focus:border-[#534AB7]"
                    style={{ borderColor: errors.expiry ? '#E24B4A' : '#E5E0D5' }}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="CVV"
                    maxLength={4}
                    value={cardData.cvv}
                    onChange={(e) => setCardData({ ...cardData, cvv: e.target.value.replace(/\D/g, '') })}
                    className="min-w-0 rounded-2xl border px-4 py-3 text-sm outline-none focus:border-[#534AB7]"
                    style={{ borderColor: errors.cvv ? '#E24B4A' : '#E5E0D5' }}
                  />
                </div>
                {(errors.expiry || errors.cvv) && <p className="text-xs text-red-500">{errors.expiry || errors.cvv}</p>}
              </>
            ) : (
              <div className="rounded-3xl bg-[#EFEDFF] p-4 text-center">
                <i className="ri-store-2-line text-4xl text-[#534AB7]" />
                <p className="mt-2 text-base font-black text-[#3C3489]">Pago en tienda</p>
                <p className="mt-2 text-xs leading-6 text-[#534AB7]">
                  Al confirmar recibiras por WhatsApp una referencia de 14 digitos para pagar en OXXO, 7-Eleven o Farmacias del Ahorro.
                </p>
              </div>
            )}

            <label className="mt-1 flex cursor-pointer items-start gap-3" onClick={() => setAceptaTerminos(!aceptaTerminos)}>
              <div
                className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-colors"
                style={{ backgroundColor: aceptaTerminos ? '#534AB7' : 'white', borderColor: aceptaTerminos ? '#534AB7' : '#D1D5DB' }}
              >
                {aceptaTerminos && <i className="ri-check-line text-xs text-white" />}
              </div>
              <span className="text-xs leading-snug text-[#625D6E]">
                He leido y acepto los <b>Terminos y Condiciones</b> y el <b>Contrato de Membresia</b> de Coverly.
              </span>
            </label>
            {errors.terminos && <p className="ml-8 text-xs text-red-500">{errors.terminos}</p>}
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-[#E8E1D3] bg-white px-5 py-4" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}>
        {errorMsg && <p className="mb-2 text-center text-xs text-red-500">{errorMsg}</p>}
        {extLoading && <p className="mb-2 text-center text-xs text-[#534AB7]">Procesando...</p>}
        <button
          onClick={handleActivar}
          className="w-full rounded-2xl bg-[#534AB7] py-4 text-base font-black text-white shadow-lg shadow-[#534AB7]/20 transition-transform active:scale-[0.98]"
        >
          Activar membresia
        </button>
      </div>
    </div>
  );
}
